"use client";
import { createContext, use } from "react";
import type {
    MicrocopyContextAPI,
    MicrocopyContextState,
    MicrocopyGetOptions,
    MicrocopySetKey,
    MicrocopySet,
} from "./types";
import type { ReactNode } from "react";
import { getMicrocopyValue, getMicrocopyValues } from "./microcopyHelpers";

export { MAX_LOGGED_KEYS, loggedKeyQueue } from "./microcopyHelpers";

const MicrocopyContext = createContext<MicrocopyContextState | undefined>(undefined);
const { Provider: MicrocopyProvider } = MicrocopyContext;

export type PageMicrocopySets = Record<string, MicrocopySet> & {
    page: MicrocopySet;
    shared?: never;
};

export const SharedMicrocopyProvider = ({
    children,
    sharedMicrocopySet,
}: {
    children: ReactNode;
    sharedMicrocopySet: MicrocopySet | null;
}) => (
    <MicrocopyProvider value={{ shared: sharedMicrocopySet ?? {}, page: {} }}>
        {children}
    </MicrocopyProvider>
);

export const PageMicrocopyProvider = ({
    children,
    microcopySets,
}: {
    children: ReactNode;
    microcopySets: PageMicrocopySets;
}) => {
    const sharedMicrocopyContext = use(MicrocopyContext);

    if (!sharedMicrocopyContext) {
        throw new Error("PageMicrocopyProvider must be used within SharedMicrocopyProvider");
    }

    return (
        <MicrocopyProvider value={{ ...microcopySets, shared: sharedMicrocopyContext.shared }}>
            {children}
        </MicrocopyProvider>
    );
};

const useMicrocopy = (): MicrocopyContextAPI => {
    const context = use(MicrocopyContext);

    if (context === undefined) {
        throw new Error("useMicrocopy must be within MicrocopyProvider");
    }

    const get = <T extends boolean = false>(
        microcopySetKey: MicrocopySetKey,
        microcopyKey: string,
        options?: MicrocopyGetOptions & { jsx?: T }
    ): T extends true ? ReactNode | null : string | null => {
        return getMicrocopyValue(context, microcopySetKey, microcopyKey, options);
    };

    const getMultiple = (
        microcopySetKey: MicrocopySetKey,
        microcopyKeys: readonly string[],
        options?: MicrocopyGetOptions
    ): Record<string, string> | null => {
        return getMicrocopyValues(context, microcopySetKey, microcopyKeys, options);
    };

    return {
        get,
        getMultiple,
    };
};

export { MicrocopyContext, MicrocopyProvider, useMicrocopy };
